import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  AccountCircle as AccountIcon,
  Login as LoginIcon,
  AddBusiness as AddBusinessIcon,
  Apartment as ApartmentIcon,
  LocalShipping as DeliveryIcon,
  Build as BuildIcon,
  Storefront as StorefrontIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './Landing.css';

/* ── Профессиональная палитра CRM/ERP ── */
const palette = {
  // Основные
  navy:       '#0b1634',   // deep navy – профессионализм
  slate:      '#192b3e',   // тёмный slate
  primary:    '#0878ff',   // royal blue – доверие
  primaryDk:  '#086bdc',
  primaryLt:  '#3B82F6',
  // Акценты
  teal:       '#0D9488',   // teal – свежесть
  amber:      '#a5652c',   // amber – внимание, CTA
  amberDk:    '#92551e',
  emerald:    '#059669',   // success
  violet:     '#a5652c',   // premium
  // Нейтральные
  bg:         '#f3f7fc',
  surface:    '#F1F5F9',
  border:     '#E2E8F0',
  textPri:    '#0b1634',
  textSec:    '#657797',
  textMuted:  '#94A3B8',
};

/* ── Reveal-on-scroll: появление блока при попадании во вьюпорт ── */
function Reveal({ children, variant = 'up', delay = 0, once = true, style, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [once]);

  const variantClass = variant === 'up' ? '' : `reveal--${variant}`;
  return (
    <div
      ref={ref}
      className={`reveal ${variantClass} ${visible ? 'reveal--visible' : ''}`.trim()}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

function Landing() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  /* ── CRM-функции (без Telegram-бота – это канал, не фича) ── */
  const crmFeatures = [
    {
      icon: <InventoryIcon sx={{ fontSize: 36 }} />,
      color: palette.primary,
      title: 'Управление активами',
      description: 'Контроль над арендным фондом: статусы, локации, доступность, амортизация'
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 36 }} />,
      color: palette.teal,
      title: 'CRM и база клиентов',
      description: 'История аренд, контакты, статистика, сегментация и автонапоминания'
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 36 }} />,
      color: palette.emerald,
      title: 'Сделки и договоры',
      description: 'Воронка продаж, автоматические оферты, акты приёма-передачи'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 36 }} />,
      color: palette.violet,
      title: 'Аналитика и отчёты',
      description: 'Доходы, загрузка активов, финансовые отчёты, прогнозирование'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 36 }} />,
      color: palette.amber,
      title: 'Автоматизация процессов',
      description: 'AI-обработка заявок, автораспределение, напоминания и уведомления'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 36 }} />,
      color: palette.primaryDk,
      title: 'Мультитенант и безопасность',
      description: 'Изолированные данные для каждой компании, роли и права доступа'
    }
  ];

  /* ── Возможности клиентского портала (только реально работающее) ── */
  const clientFeatures = [
    { icon: <HistoryIcon sx={{ fontSize: 28 }} />, text: 'История аренд и оплат' },
    { icon: <ReceiptIcon sx={{ fontSize: 28 }} />, text: 'Подписание оферт и актов' },
    { icon: <AccountIcon sx={{ fontSize: 28 }} />, text: 'Данные клиента и контакты' },
  ];

  const useCases = [
    { icon: <DeliveryIcon sx={{ fontSize: 34, color: palette.primary }} />, title: 'Прокат оборудования', desc: 'Медтехника, инструменты, электроника' },
    { icon: <ApartmentIcon sx={{ fontSize: 34, color: palette.teal }} />, title: 'Аренда недвижимости', desc: 'Квартиры, дома, коммерческие помещения' },
    { icon: <StorefrontIcon sx={{ fontSize: 34, color: palette.violet }} />, title: 'Прокат товаров', desc: 'Спортинвентарь, костюмы, техника' },
    { icon: <BuildIcon sx={{ fontSize: 34, color: palette.amber }} />, title: 'Строительная техника', desc: 'Генераторы, леса, спецтехника' },
  ];

  /* ── Частые вопросы ── */
  const faqs = [
    { q: 'Нужно ли что-то устанавливать?', a: 'Нет. ПрокатПрост — облачная платформа. Регистрируетесь, создаёте компанию и сразу работаете из браузера на любом устройстве.' },
    { q: 'Подойдёт ли сервис моему виду проката?', a: 'Да. Система универсальна: прокат оборудования и техники, аренда недвижимости, товаров, спецтехники. Каталог и тарифы настраиваются под ваш бизнес.' },
    { q: 'Что видит мой клиент?', a: 'Клиент получает доступ к личному кабинету, где видит свои активные аренды, историю оплат и может подписать оферту онлайн — без регистрации в самой CRM.' },
    { q: 'Какие каналы связи можно подключить?', a: 'Мессенджеры, электронную почту и телефонию. Все обращения собираются в едином окне, а заявки автоматически попадают в воронку сделок.' },
    { q: 'Мои данные в безопасности?', a: 'Данные каждой компании изолированы (мультитенант), доступ разграничен по ролям и правам сотрудников. Соединение защищено по HTTPS.' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', overflow: 'hidden', backgroundColor: palette.bg }}>

      {/* ════════════════ НАВБАР ════════════════ */}
      <Box sx={{ 
        backgroundColor: 'rgba(15,23,42,0.97)', 
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, color: '#fff', 
              display: 'flex', alignItems: 'center', gap: 1,
              letterSpacing: '-0.02em',
            }}>
              <Box sx={{ 
                width: 32, height: 32, borderRadius: '8px',
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.teal})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <DashboardIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
              ПрокатПрост
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button 
                size="small"
                sx={{ 
                  color: palette.textMuted, fontWeight: 500, textTransform: 'none',
                  '&:hover': { color: '#fff' },
                }}
                onClick={() => navigate('/my')}
              >
                Кабинет клиента
              </Button>
              <Button 
                size="small"
                sx={{ 
                  color: palette.textMuted, fontWeight: 500, textTransform: 'none',
                  '&:hover': { color: '#fff' },
                }}
                onClick={() => navigate('/login')}
              >
                Для сотрудников
              </Button>
              <Button 
                variant="contained" size="small"
                sx={{ 
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryLt})`,
                  fontWeight: 600, textTransform: 'none', borderRadius: '8px',
                  px: 2.5, 
                  '&:hover': { background: `linear-gradient(135deg, ${palette.primaryDk}, ${palette.primary})` },
                  boxShadow: '0 2px 12px rgba(37,99,235,0.35)',
                }}
                onClick={() => navigate('/register-founder')}
              >
                Начать бесплатно
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* ════════════════ ГЕРОЙ ════════════════ */}
      <Box sx={{ 
        background: `linear-gradient(160deg, ${palette.navy} 0%, #162044 40%, #0C2D48 70%, #0A3155 100%)`,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Декоративная сетка */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
        {/* Свечение */}
        <Box sx={{
          position: 'absolute', top: '-20%', right: '-10%',
          width: '600px', height: '600px',
          background: `radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)`,
          borderRadius: '50%',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '-30%', left: '-5%',
          width: '500px', height: '500px',
          background: `radial-gradient(circle, rgba(13,148,136,0.12) 0%, transparent 65%)`,
          borderRadius: '50%',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', py: { xs: 8, md: 12 }, color: 'white' }} className={isVisible ? 'fade-in-up' : ''}>
            <Chip 
              label="CRM + ERP для арендного бизнеса" 
              sx={{ 
                backgroundColor: 'rgba(37,99,235,0.2)', 
                color: palette.primaryLt, mb: 3, fontWeight: 600,
                fontSize: '0.85rem', py: 2.5, px: 1,
                border: '1px solid rgba(37,99,235,0.3)',
                borderRadius: '8px',
              }} 
            />
            
            <Typography variant="h2" sx={{ 
              fontWeight: 800, mb: 3,
              fontSize: { xs: '2.2rem', md: '3.4rem' },
              color: '#fff',
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
            }}>
              Платформа для управления
              <br />
              <Box component="span" sx={{ 
                background: `linear-gradient(135deg, ${palette.primaryLt}, ${palette.teal})`,
                backgroundClip: 'text', WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                арендным бизнесом
              </Box>
            </Typography>
            
            <Typography variant="h6" sx={{ 
              mb: 5, color: palette.textMuted, maxWidth: 640, mx: 'auto', lineHeight: 1.6,
              fontWeight: 400, fontSize: '1.1rem',
            }}>
              CRM-система с аналитикой, автоматизацией и клиентским порталом.
              <br />Подключайте любые каналы связи — мессенджеры, почту, телефонию.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
              <Button
                variant="contained" size="large"
                endIcon={<ArrowIcon />}
                className="cta-button"
                sx={{ 
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryLt})`,
                  px: 5, py: 1.8, fontSize: '1.05rem', fontWeight: 600,
                  textTransform: 'none', borderRadius: '10px',
                  '&:hover': { 
                    background: `linear-gradient(135deg, ${palette.primaryDk}, ${palette.primary})`, 
                    transform: 'translateY(-2px)', 
                    boxShadow: '0 8px 30px rgba(37,99,235,0.4)' 
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(37,99,235,0.3)',
                }}
                onClick={() => navigate('/register-founder')}
              >
                Создать компанию бесплатно
              </Button>
              <Button
                variant="outlined" size="large"
                sx={{ 
                  color: '#fff', borderColor: 'rgba(255,255,255,0.2)',
                  px: 4, py: 1.8, fontSize: '1.05rem', fontWeight: 500,
                  textTransform: 'none', borderRadius: '10px',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.05)' },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => navigate('/login')}
              >
                Войти в систему
              </Button>
            </Stack>

            {/* Мини-статистика */}
            <Stack direction="row" spacing={5} justifyContent="center" sx={{ mt: 6, opacity: 0.6 }}>
              {[
                { val: 'SaaS', label: 'облачная платформа' },
                { val: 'AI', label: 'автоматизация' },
                { val: '24/7', label: 'клиентский портал' },
              ].map((s, i) => (
                <Box key={i} sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>{s.val}</Typography>
                  <Typography variant="caption" sx={{ color: palette.textMuted, fontSize: '0.75rem' }}>{s.label}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* ════════════════ ДВА ПУТИ: CRM + КЛИЕНТСКИЙ ПОРТАЛ ════════════════ */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', fontWeight: 800, mb: 1.5, color: palette.textPri,
              letterSpacing: '-0.02em',
            }}>
              Два входа — одна платформа
            </Typography>
            <Typography variant="body1" sx={{ 
              textAlign: 'center', mb: 6, color: palette.textSec, maxWidth: 560, mx: 'auto',
            }}>
              Система для вашего бизнеса и портал для ваших клиентов — работают вместе
            </Typography>
          </Reveal>

          <Grid container spacing={4}>
            {/* ── Блок CRM/ERP ── */}
            <Grid item xs={12} md={6}>
              <Reveal variant="left" style={{ height: '100%' }}>
              <Paper elevation={0} sx={{ 
                p: 4, height: '100%', borderRadius: '16px',
                border: `2px solid ${palette.primary}`,
                background: `linear-gradient(160deg, rgba(37,99,235,0.03) 0%, rgba(37,99,235,0.08) 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 40px rgba(37,99,235,0.12)` },
              }}>
                <Chip label="Для бизнеса" sx={{ 
                  backgroundColor: palette.primary, color: '#fff', fontWeight: 600,
                  mb: 2.5, fontSize: '0.8rem',
                }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: palette.textPri }}>
                  CRM / ERP система
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSec, mb: 3, lineHeight: 1.7 }}>
                  Полноценная система управления арендным бизнесом. Учёт активов, 
                  CRM с клиентской базой, сделки, финансы, аналитика, сотрудники и роли.
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {['Учёт активов и каталог', 'Воронка сделок и договоры', 'Финансы и аналитика', 'Управление сотрудниками'].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon sx={{ fontSize: 18, color: palette.primary }} />
                      <Typography variant="body2" sx={{ color: palette.textPri, fontWeight: 500 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained" size="medium"
                    startIcon={<AddBusinessIcon />}
                    sx={{ 
                      background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryLt})`,
                      fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                      '&:hover': { background: `linear-gradient(135deg, ${palette.primaryDk}, ${palette.primary})` },
                      boxShadow: '0 2px 12px rgba(37,99,235,0.3)',
                    }}
                    onClick={() => navigate('/register-founder')}
                  >
                    Создать компанию
                  </Button>
                  <Button
                    variant="outlined" size="medium"
                    startIcon={<LoginIcon />}
                    sx={{ 
                      color: palette.primary, borderColor: palette.primary, 
                      fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                      '&:hover': { backgroundColor: 'rgba(37,99,235,0.06)' },
                    }}
                    onClick={() => navigate('/login')}
                  >
                    Вход для сотрудников
                  </Button>
                </Stack>
              </Paper>
              </Reveal>
            </Grid>

            {/* ── Блок Клиентский портал ── */}
            <Grid item xs={12} md={6}>
              <Reveal variant="right" style={{ height: '100%' }}>
              <Paper elevation={0} sx={{ 
                p: 4, height: '100%', borderRadius: '16px',
                border: `2px solid ${palette.teal}`,
                background: `linear-gradient(160deg, rgba(13,148,136,0.03) 0%, rgba(13,148,136,0.08) 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 40px rgba(13,148,136,0.12)` },
              }}>
                <Chip label="Для клиентов вашей компании" sx={{ 
                  backgroundColor: palette.teal, color: '#fff', fontWeight: 600,
                  mb: 2.5, fontSize: '0.8rem',
                }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: palette.textPri }}>
                  Клиентский портал
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSec, mb: 3, lineHeight: 1.7 }}>
                  Личный кабинет для клиентов вашего бизнеса. Они видят свои активные аренды, 
                  историю оплат и могут подписать оферту онлайн.
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {clientFeatures.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: palette.teal }}>{item.icon}</Box>
                      <Typography variant="body2" sx={{ color: palette.textPri, fontWeight: 500 }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  variant="contained" size="medium"
                  startIcon={<AccountIcon />}
                  sx={{ 
                    backgroundColor: palette.teal,
                    fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                    '&:hover': { backgroundColor: '#0F766E' },
                    boxShadow: '0 2px 12px rgba(13,148,136,0.3)',
                  }}
                  onClick={() => navigate('/my')}
                >
                  Личный кабинет клиента
                </Button>
              </Paper>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ════════════════ ДЛЯ КОГО ════════════════ */}
      <Box sx={{ backgroundColor: palette.surface, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', fontWeight: 800, mb: 1, color: palette.textPri,
              letterSpacing: '-0.02em',
            }}>
              Подходит для любого арендного бизнеса
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 5, color: palette.textSec }}>
              Создайте компанию, добавьте товары — и система готова к работе
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {useCases.map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Reveal variant="zoom" delay={i * 90}>
                <Paper elevation={0} sx={{ 
                  p: 3, textAlign: 'center',
                  backgroundColor: '#fff',
                  border: `1px solid ${palette.border}`,
                  borderRadius: '14px',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    borderColor: palette.primary,
                    transform: 'translateY(-4px)', 
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)' 
                  }
                }}>
                  <Box sx={{ 
                    mb: 2, width: 56, height: 56, borderRadius: '12px',
                    backgroundColor: palette.surface, mx: 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: palette.textPri }}>{item.title}</Typography>
                  <Typography variant="caption" sx={{ color: palette.textSec, lineHeight: 1.5, display: 'block' }}>{item.desc}</Typography>
                </Paper>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ════════════════ КАК ЭТО РАБОТАЕТ ════════════════ */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', fontWeight: 800, mb: 5, color: palette.textPri,
              letterSpacing: '-0.02em',
            }}>
              Как начать работу
            </Typography>
          </Reveal>
          <Grid container spacing={4}>
            {[
              { step: '01', title: 'Создайте компанию', desc: 'Зарегистрируйтесь и укажите название и тип бизнеса', color: palette.primary },
              { step: '02', title: 'Добавьте активы', desc: 'Загрузите каталог товаров с ценами и описаниями', color: palette.teal },
              { step: '03', title: 'Настройте каналы', desc: 'Подключите мессенджеры, почту или телефонию для клиентов', color: palette.violet },
              { step: '04', title: 'Управляйте и растите', desc: 'Сделки, оплаты и аналитика — всё в одной системе', color: palette.emerald },
            ].map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Reveal delay={i * 110}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 800, color: item.color, opacity: 0.2,
                    mb: 1.5, fontSize: '2.5rem',
                  }}>
                    {item.step}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: palette.textPri }}>{item.title}</Typography>
                  <Typography variant="body2" sx={{ color: palette.textSec }}>{item.desc}</Typography>
                </Box>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ════════════════ ВОЗМОЖНОСТИ CRM ════════════════ */}
      <Box sx={{ 
        background: `linear-gradient(160deg, ${palette.navy} 0%, #162044 50%, #0C2D48 100%)`,
        py: { xs: 6, md: 10 }, position: 'relative', overflow: 'hidden',
      }}>
        {/* Сетка на фоне */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.025) 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <Typography variant="h3" sx={{ 
              textAlign: 'center', fontWeight: 800, mb: 1.5, color: '#fff',
              fontSize: { xs: '1.8rem', md: '2.4rem' }, letterSpacing: '-0.02em',
            }}>
              Инструменты для управления арендой
            </Typography>
            <Typography variant="body1" sx={{ 
              textAlign: 'center', mb: 6, color: palette.textMuted, maxWidth: 520, mx: 'auto',
            }}>
              Полный набор функций в одной системе
            </Typography>
          </Reveal>

          <Grid container spacing={3}>
            {crmFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Reveal variant="zoom" delay={(index % 3) * 90}>
                <Card elevation={0} className="feature-card" sx={{ 
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '14px',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-4px)', 
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    borderColor: 'rgba(255,255,255,0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ 
                      width: 48, height: 48, borderRadius: '12px',
                      backgroundColor: `${feature.color}20`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 2, color: feature.color,
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#fff', fontSize: '1rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.textMuted, lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ════════════════ FAQ ════════════════ */}
      <Box sx={{ backgroundColor: palette.surface, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Reveal>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', fontWeight: 800, mb: 1, color: palette.textPri,
              letterSpacing: '-0.02em',
            }}>
              Частые вопросы
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 5, color: palette.textSec }}>
              Коротко о главном перед стартом
            </Typography>
          </Reveal>
          {faqs.map((f, i) => (
            <Reveal key={i} delay={i * 60}>
              <Accordion 
                disableGutters elevation={0}
                sx={{ 
                  mb: 1.5, borderRadius: '12px !important', overflow: 'hidden',
                  border: `1px solid ${palette.border}`, backgroundColor: '#fff',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: palette.primary }} />}
                  sx={{ px: 3, py: 0.5, '& .MuiAccordionSummary-content': { my: 1.5 } }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: palette.textPri }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 2.5, pt: 0 }}>
                  <Typography variant="body2" sx={{ color: palette.textSec, lineHeight: 1.7 }}>{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            </Reveal>
          ))}
        </Container>
      </Box>

      {/* ════════════════ CTA ════════════════ */}
      <Box sx={{ 
        backgroundColor: '#fff', py: { xs: 6, md: 8 },
      }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <AutoIcon sx={{ fontSize: 40, color: palette.primary, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, color: palette.textPri, letterSpacing: '-0.02em' }}>
            Готовы начать?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: palette.textSec, lineHeight: 1.6 }}>
            Создайте компанию бесплатно и запустите 
            свой арендный бизнес в CRM уже сегодня
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained" size="large"
              endIcon={<ArrowIcon />}
              className="cta-button"
              sx={{ 
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryLt})`,
                px: 5, py: 1.8, fontSize: '1.05rem', fontWeight: 600,
                textTransform: 'none', borderRadius: '10px',
                '&:hover': { 
                  background: `linear-gradient(135deg, ${palette.primaryDk}, ${palette.primary})`,
                  transform: 'translateY(-2px)', 
                  boxShadow: `0 8px 24px rgba(37,99,235,0.3)` 
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 16px rgba(37,99,235,0.25)',
              }}
              onClick={() => navigate('/register-founder')}
            >
              Создать компанию
            </Button>
            <Button
              variant="outlined" size="large"
              sx={{ 
                color: palette.textPri, borderColor: palette.border,
                px: 4, py: 1.8, fontSize: '1.05rem', fontWeight: 500,
                textTransform: 'none', borderRadius: '10px',
                '&:hover': { borderColor: palette.primary, backgroundColor: 'rgba(37,99,235,0.04)' },
              }}
              onClick={() => navigate('/my')}
            >
              Клиентский портал
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ════════════════ ФУТЕР ════════════════ */}
      <Box sx={{ backgroundColor: palette.navy, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: palette.textMuted, fontSize: '0.8rem' }}>
              © 2026 ПрокатПрост — сервис аренды без залога
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button size="small" sx={{ color: palette.textMuted, textTransform: 'none', fontSize: '0.8rem', '&:hover': { color: '#fff' } }} onClick={() => navigate('/my')}>
                Кабинет клиента
              </Button>
              <Button size="small" sx={{ color: palette.textMuted, textTransform: 'none', fontSize: '0.8rem', '&:hover': { color: '#fff' } }} onClick={() => navigate('/login')}>
                Для сотрудников
              </Button>
              <Button size="small" sx={{ color: palette.textMuted, textTransform: 'none', fontSize: '0.8rem', '&:hover': { color: '#fff' } }} onClick={() => navigate('/register-founder')}>
                Создать компанию
              </Button>
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 3, pt: 3, borderTop: `1px solid rgba(255,255,255,0.08)` }}>
            <Button size="small" sx={{ color: palette.textMuted, textTransform: 'none', fontSize: '0.78rem', '&:hover': { color: '#fff' } }} onClick={() => navigate('/docs/terms')}>
              Пользовательское соглашение
            </Button>
            <Button size="small" sx={{ color: palette.textMuted, textTransform: 'none', fontSize: '0.78rem', '&:hover': { color: '#fff' } }} onClick={() => navigate('/docs/privacy')}>
              Политика конфиденциальности
            </Button>
            <Button size="small" sx={{ color: palette.textMuted, textTransform: 'none', fontSize: '0.78rem', '&:hover': { color: '#fff' } }} onClick={() => navigate('/docs/license')}>
              Лицензионный договор
            </Button>
          </Box>
        </Container>
      </Box>

    </Box>
  );
}

export default Landing;
